import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Field, Input, Select, money } from '@busbook/ui';
import { TENANT_SLUG_MAX, toTenantSlug } from '@busbook/shared';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer, StatusBadge } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

/**
 * The platform's root domain, for showing an operator what their address will be.
 *
 * Derived from where this console is served rather than configured: the console
 * always runs at `platform.{BASE_DOMAIN}` (and `console.{BASE_DOMAIN}` locally), so
 * dropping the first label is the base domain. That keeps it correct in production
 * and in local development without a build-time value that could disagree with the
 * API's own BASE_DOMAIN.
 */
const BASE_DOMAIN =
  typeof window === 'undefined'
    ? ''
    : window.location.hostname.split('.').slice(1).join('.') || window.location.hostname;

/** Tenant list and the create-tenant wizard (docs/07 "/tenants"). */

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  currency: string;
  timezone: string;
  branding: { appName: string; colorPrimary: string; logoUrl: string | null } | null;
  contracts: Array<{ type: string; commissionBps: number | null; monthlyFeeCents: number | null }>;
  _count: { bookings: number };
}

const TIMEZONES = [
  'Africa/Lagos', 'Africa/Nairobi', 'Africa/Accra', 'Africa/Johannesburg',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Istanbul',
  'America/New_York', 'America/Sao_Paulo', 'Asia/Dubai', 'Asia/Kolkata', 'UTC',
];

export function Tenants() {
  const t = useT();
  const [status, setStatus] = useState('');
  const params = new URLSearchParams({ pageSize: '100' });
  if (status) params.set('status', status);
  const tenants = useResource<TenantRow[]>(`/platform/tenants?${params.toString()}`);
  const [wizard, setWizard] = useState(false);

  const columns: Column<TenantRow>[] = [
    {
      key: 'name',
      header: 'Tenant',
      render: (row) => (
        <div className="flex items-center gap-2.5">
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-[0.6875rem] font-bold text-white"
            style={{ background: row.branding?.colorPrimary ?? 'var(--color-primary)' }}
            aria-hidden
          >
            {(row.branding?.appName ?? row.name).slice(0, 1).toUpperCase()}
          </span>
          <div>
            <Link to={`/tenants/${row.id}`} className="font-medium underline-offset-2 hover:underline">
              {row.name}
            </Link>
            <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
              {row.slug}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (row) => {
        const contract = row.contracts[0];
        if (!contract) return <span style={{ color: 'var(--color-faint)' }}>none</span>;
        const parts: string[] = [];
        if (contract.commissionBps !== null) parts.push(`${(contract.commissionBps / 100).toFixed(2).replace(/\.?0+$/, '')}%`);
        if (contract.monthlyFeeCents !== null) parts.push(money(contract.monthlyFeeCents, row.currency));
        return (
          <span>
            {contract.type.toLowerCase()}
            <span style={{ color: 'var(--color-muted)' }}> · {parts.join(' + ')}</span>
          </span>
        );
      },
    },
    { key: 'locale', header: 'Locale', render: (row) => `${row.currency} · ${row.timezone}` },
    { key: 'bookings', header: 'Bookings', numeric: true, render: (row) => row._count.bookings },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
  ];

  return (
    <Page
      title={t('tenants.title')}
      description={t('tenants.subtitle')}
      actions={
        <div className="flex items-center gap-2">
          <Select
            aria-label={t('common.filterStatus')}
            className="h-9 w-40"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">{t('common.allStatuses')}</option>
            <option value="ONBOARDING">Onboarding</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
          </Select>
          <Button onClick={() => setWizard(true)}>{t('tenants.new')}</Button>
        </div>
      }
    >
      <DataTable
        columns={columns}
        rows={tenants.data}
        rowKey={(row) => row.id}
        loading={tenants.loading}
        error={tenants.error}
        empty={{ title: 'No tenants yet', description: 'Create the first bus company to get started.' }}
      />

      {wizard && (
        <CreateTenantWizard
          onClose={() => setWizard(false)}
          onCreated={async () => {
            setWizard(false);
            await tenants.reload();
          }}
        />
      )}
    </Page>
  );
}

function CreateTenantWizard({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const t = useT();
  const [form, setForm] = useState({
    name: '',
    slug: '',
    timezone: 'Africa/Lagos',
    currency: 'NGN',
    ownerName: '',
    ownerEmail: '',
    type: 'COMMISSION' as 'COMMISSION' | 'MONTHLY' | 'HYBRID',
    commissionPercent: '5',
    monthlyFee: '',
    effectiveFrom: new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ previewUrl: string; ownerEmail: string } | null>(null);

  const needsCommission = form.type === 'COMMISSION' || form.type === 'HYBRID';
  const needsFee = form.type === 'MONTHLY' || form.type === 'HYBRID';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ previewUrl: string; tenant: { id: string } }>(
        '/platform/tenants',
        {
          name: form.name.trim(),
          slug: form.slug.trim().toLowerCase(),
          timezone: form.timezone,
          currency: form.currency.toUpperCase(),
          owner: { name: form.ownerName.trim(), email: form.ownerEmail.trim() },
          contract: {
            type: form.type,
            ...(needsCommission
              ? { commissionBps: Math.round(Number(form.commissionPercent) * 100) }
              : {}),
            ...(needsFee ? { monthlyFeeCents: Math.round(Number(form.monthlyFee) * 100) } : {}),
            effectiveFrom: form.effectiveFrom,
          },
        },
      );
      setCreated({ previewUrl: result.previewUrl, ownerEmail: form.ownerEmail });
    } catch (err) {
      setError(errorMessage(err, 'Could not create this tenant.'));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <Drawer
        open
        title={t('wizard.createdTitle')}
        onClose={() => void onCreated()}
        footer={<Button onClick={() => void onCreated()}>Done</Button>}
      >
        <div className="flex flex-col gap-4">
          <Alert tone="success" title={t('tenants.onboardingStarted')}>
            An invitation has been issued to {created.ownerEmail}. They set their own password from
            the link.
          </Alert>
          <div>
            <p className="text-sm font-medium">Preview link</p>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
              The booking site is not public until go-live. Share this link so they can review it.
            </p>
            <code
              className="mt-2 block break-all rounded-lg border p-2 text-xs"
              style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
            >
              {created.previewUrl}
            </code>
          </div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      open
      title={t('wizard.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={busy}
            disabled={
              !form.name ||
              form.slug.length < 3 ||
              !form.ownerEmail ||
              !form.ownerName
            }
          >
            Create tenant
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error && <Alert tone="error">{error}</Alert>}

        <section className="flex flex-col gap-4">
          <p className="text-sm font-semibold">1 · Company</p>
          <Field label={t('wizard.companyName')} htmlFor="w-name">
            <Input
              id="w-name"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value;
                setForm((current) => ({
                  ...current,
                  name,
                  // Suggest a subdomain, but leave it editable. Same normaliser as
                  // the field itself, so the suggestion is always a valid one.
                  slug: current.slug || toTenantSlug(name),
                }));
              }}
            />
          </Field>
          <Field
            label={t('wizard.subdomain')}
            htmlFor="w-slug"
            hint={
              form.slug
                ? t('wizard.subdomainPreview', { host: `${form.slug}.${BASE_DOMAIN}` })
                : t('wizard.subdomainHint')
            }
          >
            <Input
              id="w-slug"
              value={form.slug}
              maxLength={TENANT_SLUG_MAX}
              /**
               * Normalised as it is typed, not on submit. This field becomes a DNS
               * label, and letting an operator type something a hostname cannot
               * contain only to reject it afterwards — naming a field they were no
               * longer looking at — is how this went wrong before.
               */
              onChange={(e) => setForm({ ...form, slug: toTenantSlug(e.target.value) })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('wizard.timezone')} htmlFor="w-tz">
              <Select id="w-tz" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
                {TIMEZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone.replace(/_/g, ' ')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('wizard.currency')} htmlFor="w-cur">
              <Input
                id="w-cur"
                className="tabular"
                maxLength={3}
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
              />
            </Field>
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <p className="text-sm font-semibold">2 · First owner</p>
          <Field label={t('wizard.ownerName')} htmlFor="w-oname">
            <Input id="w-oname" value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} />
          </Field>
          <Field label={t('wizard.ownerEmail')} htmlFor="w-oemail" hint="They receive an invitation to set a password.">
            <Input
              id="w-oemail"
              type="email"
              value={form.ownerEmail}
              onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
            />
          </Field>
        </section>

        <section className="flex flex-col gap-4">
          <p className="text-sm font-semibold">3 · Contract</p>
          <Field label="Type" htmlFor="w-type">
            <Select
              id="w-type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as typeof form.type })}
            >
              <option value="COMMISSION">Commission per booking</option>
              <option value="MONTHLY">Flat monthly fee</option>
              <option value="HYBRID">Both</option>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            {needsCommission && (
              <Field label="Commission (%)" htmlFor="w-comm">
                <Input
                  id="w-comm"
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  className="tabular"
                  value={form.commissionPercent}
                  onChange={(e) => setForm({ ...form, commissionPercent: e.target.value })}
                />
              </Field>
            )}
            {needsFee && (
              <Field label={`Monthly fee (${form.currency})`} htmlFor="w-fee">
                <Input
                  id="w-fee"
                  type="number"
                  step="0.01"
                  min={0}
                  className="tabular"
                  value={form.monthlyFee}
                  onChange={(e) => setForm({ ...form, monthlyFee: e.target.value })}
                />
              </Field>
            )}
          </div>
          <Field label="In effect from" htmlFor="w-from">
            <Input
              id="w-from"
              type="date"
              value={form.effectiveFrom}
              onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
            />
          </Field>
        </section>
      </div>
    </Drawer>
  );
}
